/**
 * Ampli Configuration & Initialization
 *
 * This file provides environment-aware initialization for the Ampli analytics wrapper.
 * The generated code in @/shared/lib/ampli/index.ts should NOT be edited manually.
 *
 * @see https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/ampli/
 */

import { ampli, ApiKey } from '@/shared/lib/ampli'
import { pageViewTrackingEnrichment } from '@/shared/lib/ampli-plugins/router'

/**
 * Determines if tracking should be disabled based on hostname.
 * Production hosts only - events are not sent from staging/dev/localhost.
 */
export const ampliDisabled = ['staging', 'dev', 'localhost'].some((e) =>
	window.location.hostname.includes(e)
)

/**
 * Current Ampli environment based on hostname.
 * Only 'production' is defined in the generated code.
 */
export const ampliEnvironment: 'production' | 'development' = ampliDisabled
	? 'development'
	: 'production'

/**
 * Initializes the Ampli instance with environment-aware configuration.
 *
 * Features:
 * - Production: Events sent to Amplitude, minimal logging
 * - Development: Events NOT sent, debug logging enabled for validation
 *
 * Call this once during app initialization.
 *
 * @example
 * ```typescript
 * import { initializeAmpli } from '@/app/config/ampli'
 *
 * // In app initialization
 * initializeAmpli()
 * ```
 */
export function initializeAmpli(): void {
	if (!ampliDisabled) {
		ampli.load({
			environment: 'production',
			disabled: ampliDisabled,
			client: {
				apiKey: ApiKey.production,
				configuration: {
					logLevel: 0,
				},
			},
		})
		ampli.client.add(pageViewTrackingEnrichment())
	}
}
